import React, { useEffect, useRef } from 'react';
import { useGetCompanyQuery } from '../../redux/features/apiSlices/setup/company';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setPrintData } from '../../redux/features/slices/modeSlice';

const VoucherPrint = ({ data: propData }) => {
    const { data: companyData } = useGetCompanyQuery();
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const printRef = useRef();

    // Get data from props, location state, or localStorage
    const voucherData = propData || location.state?.voucherData || JSON.parse(localStorage.getItem('lastVoucherData'));

    useEffect(() => {
        if (!voucherData) {
            console.error("No voucher data found for printing");
            return;
        }

        const timer = setTimeout(() => {
            window.print();
            if (propData) {
                setTimeout(() => {
                    dispatch(setPrintData(null));
                }, 500);
            }
        }, 1200);

        return () => clearTimeout(timer);
    }, [voucherData, propData, dispatch]);

    if (!voucherData) {
        return <div className="p-10 text-center font-bold text-red-500">No voucher data found.</div>;
    }

    const {
        voucherNo,
        voucherDate,
        referenceNo,
        description,
        totalVoucherAmount,
        paymentMode,
        bankDetails,
        account,
        vendorPayments,
        customerPayments,
        entries,
        type
    } = voucherData;

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const formatAmount = (amount) => {
        return Number(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const getVoucherTitle = () => {
        switch (type) {
            case 'payment': return 'Payment Voucher';
            case 'receive': return 'Receipt Voucher';
            case 'journal': return 'Journal Voucher';
            case 'patty': return 'Petty Cash Voucher';
            case 'purchase': return 'Purchase Payment';
            default: return 'Voucher';
        }
    };

    return (
        <div className="bg-gray-100 min-h-screen p-4 md:p-10 print:p-0 print:bg-white font-sans text-gray-800" ref={printRef}>
            {/* Action Buttons */}
            <div className="flex justify-center gap-4 mb-6 print:hidden">
                <button
                    onClick={() => window.print()}
                    className="bg-slate-800 text-white px-6 py-2 rounded shadow hover:bg-slate-700 transition-all text-sm font-semibold"
                >
                    Print Document
                </button>
                <button
                    onClick={() => navigate(-1)}
                    className="bg-white text-slate-700 px-6 py-2 rounded shadow border border-slate-200 hover:bg-slate-50 transition-all text-sm font-semibold"
                >
                    Back to System
                </button>
            </div>

            {/* Voucher Document */}
            <div className="max-w-[210mm] mx-auto bg-white shadow-xl print:shadow-none border border-gray-200 print:border-none p-8 md:p-12 overflow-hidden min-h-[297mm] flex flex-col">

                {/* Header: Company Info and Document Title */}
                <div className="flex justify-between items-start border-b-2 border-slate-800 pb-6 mb-6">
                    <div className="flex gap-6">
                        {companyData?.logo && (
                            <img
                                src={`${process.env.REACT_APP_BASE_URL}/${companyData.logo}`}
                                alt="Logo"
                                className="w-24 h-24 object-contain"
                            />
                        )}
                        <div className="max-w-md">
                            <h1 className="text-2xl font-bold text-slate-900 uppercase mb-1">
                                {companyData?.company || 'IB Traders'}
                            </h1>
                            <p className="text-xs text-gray-600 leading-tight mb-2">
                                {companyData?.address || 'Pakistan Office Address'}
                            </p>
                            <div className="text-[10px] text-gray-500 uppercase tracking-tight">
                                <span>Phone: {companyData?.phone || 'N/A'}</span>
                                <span className="mx-2">|</span>
                                <span>Email: {companyData?.email || 'N/A'}</span>
                            </div>
                        </div>
                    </div>
                    <div className="text-right">
                        <h2 className="text-xl font-bold text-slate-800 uppercase tracking-tight mb-4 border-b border-slate-200 pb-1">
                            {getVoucherTitle()}
                        </h2>
                        <div className="space-y-1">
                            <div className="flex justify-end gap-2 text-xs">
                                <span className="font-semibold text-gray-500">Voucher No:</span>
                                <span className="font-bold text-slate-900">{voucherNo}</span>
                            </div>
                            <div className="flex justify-end gap-2 text-xs">
                                <span className="font-semibold text-gray-500">Date:</span>
                                <span className="font-bold text-slate-900">{formatDate(voucherDate)}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Secondary Metadata */}
                <div className="grid grid-cols-3 gap-6 mb-8">
                    <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Voucher Type</p>
                        <p className="text-sm font-bold text-slate-800 uppercase">
                            {type === 'payment' ? 'Payment Voucher' :
                                type === 'receive' ? 'Receipt Voucher' :
                                    type === 'journal' ? 'Journal Voucher' :
                                        type === 'patty' ? 'Petty Cash Voucher' : 'Voucher'}
                        </p>
                    </div>
                    <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Payment Source</p>
                        <p className="text-sm font-bold text-slate-800 capitalize">
                            {account?.name || (vendorPayments?.[0]?.accountName) || (customerPayments?.[0]?.accountName) || 'N/A'}
                        </p>
                    </div>
                    <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Method / Mode</p>
                        <div className="flex items-center gap-2">
                            <p className="text-sm font-bold text-slate-800 capitalize">
                                {paymentMode || (vendorPayments?.[0]?.paymentMode) || (customerPayments?.[0]?.paymentMode) || 'Cash'}
                            </p>
                            {(paymentMode === 'bank' || (vendorPayments?.[0]?.paymentMode === 'bank')) && 
                             (bankDetails || vendorPayments?.[0]?.bankDetails || customerPayments?.[0]?.bankDetails) && (
                                <span className="text-[9px] bg-slate-100 px-2 py-0.5 rounded text-slate-500 font-mono">
                                    #{bankDetails?.checkNo || vendorPayments?.[0]?.bankDetails?.checkNo || customerPayments?.[0]?.checkNo || customerPayments?.[0]?.bankDetails?.checkNo}
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Transaction Table */}
                <div className="flex-1">
                    <table className="w-full border-collapse">
                        <thead>
                            <tr className="bg-slate-50 border-y border-slate-200">
                                <th className="py-2 px-3 text-left text-[10px] font-bold uppercase text-slate-600 w-12">Sr.</th>
                                <th className="py-2 px-3 text-left text-[10px] font-bold uppercase text-slate-600">Account / Description</th>
                                <th className="py-2 px-3 text-right text-[10px] font-bold uppercase text-slate-600 w-32">Debit</th>
                                <th className="py-2 px-3 text-right text-[10px] font-bold uppercase text-slate-600 w-32">Credit</th>
                            </tr>
                        </thead>
                        <tbody className="text-xs">
                            {/* VENDOR PAYMENTS */}
                            {vendorPayments && vendorPayments.map((vp, idx) => (
                                <React.Fragment key={`vp-${idx}`}>
                                    {/* Debit: Vendor */}
                                    <tr className="border-b border-slate-50">
                                        <td className="py-3 px-3 align-top text-slate-400">{(idx * 2 + 1).toString().padStart(2, '0')}</td>
                                        <td className="py-3 px-3">
                                            <p className="font-bold text-slate-900 uppercase">
                                                {vp.vendor?.name || vp.vendorName || 'Vendor Payment'}
                                                {(vp.vendor?.code || vp.vendorCode) ? ` (${vp.vendor?.code || vp.vendorCode})` : ''}
                                            </p>
                                            {/* Show PO Number if exists, otherwise show general description */}
                                            {vp.orders && vp.orders.length > 0 ? (
                                                <p className="text-[10px] text-slate-500 mt-1 italic">
                                                    Ref PO: {vp.orders.map(o => o.pono || o.bono).filter(Boolean).join(', ')}
                                                </p>
                                            ) : (
                                                <p className="text-[10px] text-slate-500 mt-1 italic">{description || 'Payment on account'}</p>
                                            )}
                                        </td>
                                        <td className="py-3 px-3 text-right font-semibold text-slate-900">{formatAmount(vp.totalPaidAmount || vp.amount)}</td>
                                        <td className="py-3 px-3 text-right text-slate-300">--</td>
                                    </tr>
                                    {/* Credit: Source Account */}
                                    <tr className="border-b border-slate-100 bg-slate-50/30">
                                        <td className="py-2 px-3 align-top text-slate-400">{(idx * 2 + 2).toString().padStart(2, '0')}</td>
                                        <td className="py-2 px-3">
                                            <p className="text-slate-600 uppercase text-[11px] ml-4 italic">
                                                To {vp.accountName || account?.name || 'Cash/Bank Account'}
                                                {(vp.accountCode || account?.code) ? ` (${vp.accountCode || account?.code})` : ''}
                                            </p>
                                            {referenceNo && (
                                                <p className="text-[9px] text-slate-400 mt-1 ml-4 italic">Ref No: {referenceNo}</p>
                                            )}
                                        </td>
                                        <td className="py-2 px-3 text-right text-slate-300">--</td>
                                        <td className="py-2 px-3 text-right font-semibold text-slate-600">{formatAmount(vp.totalPaidAmount || vp.amount)}</td>
                                    </tr>
                                </React.Fragment>
                            ))}

                            {/* CUSTOMER PAYMENTS */}
                            {customerPayments && customerPayments.map((cp, idx) => (
                                <React.Fragment key={`cp-${idx}`}>
                                    {/* Debit: Source Account */}
                                    <tr className="border-b border-slate-50">
                                        <td className="py-3 px-3 align-top text-slate-400">{(idx * 2 + 1).toString().padStart(2, '0')}</td>
                                        <td className="py-3 px-3">
                                            <p className="font-bold text-slate-900 uppercase">
                                                {cp.accountName || account?.name || 'Cash/Bank Account'}
                                                {cp.accountCode || account?.code ? ` (${cp.accountCode || account?.code})` : ''}
                                            </p>
                                            <p className="text-[9px] text-slate-500 mt-1 italic">Received From {cp.customer?.name || cp.customerName}</p>
                                        </td>
                                        <td className="py-3 px-3 text-right font-semibold text-slate-900">{formatAmount(cp.totalPaidAmount || cp.amount)}</td>
                                        <td className="py-3 px-3 text-right text-slate-300">--</td>
                                    </tr>
                                    {/* Credit: Customer */}
                                    <tr className="border-b border-slate-100 bg-slate-50/30">
                                        <td className="py-2 px-3 align-top text-slate-400">{(idx * 2 + 2).toString().padStart(2, '0')}</td>
                                        <td className="py-2 px-3">
                                            <p className="text-slate-600 uppercase text-[11px] ml-4 italic">
                                                By {cp.customer?.name || cp.customerName || 'Customer Receipt'}
                                                {cp.customer?.code || cp.customerCode ? ` (${cp.customer?.code || cp.customerCode})` : ''}
                                            </p>
                                            {cp.orders && cp.orders.length > 0 && (
                                                <p className="text-[9px] text-slate-400 mt-1 ml-4 italic">Invoices: {cp.orders.map(o => o.bono).filter(Boolean).join(', ')}</p>
                                            )}
                                        </td>
                                        <td className="py-2 px-3 text-right text-slate-300">--</td>
                                        <td className="py-2 px-3 text-right font-semibold text-slate-600">{formatAmount(cp.totalPaidAmount || cp.amount)}</td>
                                    </tr>
                                </React.Fragment>
                            ))}

                            {/* JOURNAL ENTRIES */}
                            {entries && entries.map((entry, idx) => (
                                <tr key={idx} className="border-b border-slate-50">
                                    <td className="py-3 px-3 align-top text-slate-400">{(idx + 1).toString().padStart(2, '0')}</td>
                                    <td className="py-3 px-3">
                                        <p className="font-bold text-slate-900 uppercase">{entry.account}</p>
                                        <p className="text-[10px] text-slate-500 mt-1 italic">{entry.description}</p>
                                    </td>
                                    <td className="py-3 px-3 text-right font-semibold text-slate-900">
                                        {entry.paymentType === 'debit' ? formatAmount(entry.amount) : '--'}
                                    </td>
                                    <td className="py-3 px-3 text-right font-semibold text-slate-900">
                                        {entry.paymentType === 'credit' ? formatAmount(entry.amount) : '--'}
                                    </td>
                                </tr>
                            ))}

                            {/* SINGLE ITEM FALLBACK */}
                            {!vendorPayments && !customerPayments && !entries && (
                                <React.Fragment>
                                    {/* Debit Side */}
                                    <tr className="border-b border-slate-50">
                                        <td className="py-3 px-3 text-slate-400">01</td>
                                        <td className="py-3 px-3">
                                            <p className="font-bold text-slate-900 uppercase">
                                                {type === 'patty' ? account?.name : (description || 'Transaction')}
                                            </p>
                                            <p className="text-[10px] text-slate-500 mt-1 italic">Debit Transaction</p>
                                        </td>
                                        <td className="py-3 px-3 text-right font-semibold text-slate-900">
                                            {formatAmount(totalVoucherAmount)}
                                        </td>
                                        <td className="py-3 px-3 text-right text-slate-300">--</td>
                                    </tr>
                                    {/* Credit Side */}
                                    <tr className="border-b border-slate-100 bg-slate-50/30">
                                        <td className="py-2 px-3 text-slate-400">02</td>
                                        <td className="py-2 px-3">
                                            <p className="text-slate-600 uppercase text-[11px] ml-4 italic">
                                                {paymentMode === 'cash' ? 'By Cash Account' : 'By Bank Account'}
                                            </p>
                                        </td>
                                        <td className="py-2 px-3 text-right text-slate-300">--</td>
                                        <td className="py-2 px-3 text-right font-semibold text-slate-600">
                                            {formatAmount(totalVoucherAmount)}
                                        </td>
                                    </tr>
                                </React.Fragment>
                            )}
                        </tbody>
                    </table>

                    {/* Totals Section */}
                    <div className="flex justify-end mt-4">
                        <div className="w-64">
                            <div className="flex justify-between py-2 px-3 bg-slate-800 text-white font-bold rounded">
                                <span className="text-[10px] uppercase">Net Total (PKR)</span>
                                <span>{formatAmount(totalVoucherAmount)}</span>
                            </div>
                        </div>
                    </div>

                    {/* Amount in Words */}
                    <div className="mt-6 border border-gray-100 p-3 bg-gray-50 rounded">
                        <p className="text-[9px] font-bold text-gray-400 uppercase mb-1">Amount in Words</p>
                        <p className="text-xs font-bold text-slate-800 capitalize italic">
                            PKR {formatAmount(totalVoucherAmount)} Only
                        </p>
                    </div>

                    {/* Narration */}
                    <div className="mt-4">
                        <p className="text-[9px] font-bold text-gray-400 uppercase mb-1">Narration / Remarks</p>
                        <p className="text-xs text-gray-600 leading-relaxed min-h-[40px]">
                            {description || 'No additional remarks provided.'}
                        </p>
                    </div>
                </div>

                {/* Footer Signatures */}
                <div className="mt-auto pt-10 border-t border-gray-100">
                    <div className="grid grid-cols-3 gap-12">
                        <div className="text-center">
                            <div className="border-b border-gray-300 h-8 mb-2" />
                            <p className="text-[10px] font-bold text-gray-500 uppercase">Prepared By</p>
                        </div>
                        <div className="text-center">
                            <div className="border-b border-gray-300 h-8 mb-2" />
                            <p className="text-[10px] font-bold text-gray-500 uppercase">Verified By</p>
                        </div>
                        <div className="text-center">
                            <div className="border-b border-gray-300 h-8 mb-2" />
                            <p className="text-[10px] font-bold text-gray-500 uppercase">Authorized By</p>
                        </div>
                    </div>

                    <div className="mt-8 flex justify-between items-center text-[8px] text-gray-400 font-medium">
                        <p>Printed on: {new Date().toLocaleString()} | ID: {voucherNo}</p>
                        <p className="uppercase tracking-widest">Digital System Generated Document</p>
                    </div>
                </div>
            </div>

            <style dangerouslySetInnerHTML={{
                __html: `
                @media print {
                    @page {
                        size: A4;
                        margin: 0mm !important;
                    }
                    body {
                        background: white !important;
                        margin: 0 !important;
                        padding: 0 !important;
                        -webkit-print-color-adjust: exact !important;
                    }
                    .print\\:hidden { display: none !important; }
                    .print\\:shadow-none { box-shadow: none !important; }
                    .print\\:border-none { border: none !important; }
                }
            `}} />
        </div>
    );
};

export default VoucherPrint;
